import { useEffect, useMemo, useState, type CSSProperties } from "react"
import { api } from "../../lib/api"
import type { Flashcard, Flashcards as Resposta } from "../../types"
import { Botao } from "../ui"

const CHAVE = "carteiranautica.flashcards.v1"

/**
 * Os "100 números que caem": cartão que vira ao toque, embaralhado, com
 * "sei" e "rever". São os números das normas, não as questões — nada aqui
 * vaza gabarito. Bônus da preparação completa; quem não a tem não vê nada.
 *
 * O progresso fica só no navegador (localStorage): é conveniência, pode
 * sumir, e a tela funciona sem ele.
 */
export default function Flashcards() {
  const [dados, setDados] = useState<Resposta | null>(null)
  const [aberto, setAberto] = useState(false)

  useEffect(() => {
    api.get<Resposta>("/flashcards").then(setDados).catch(() => {})
  }, [])

  if (!dados || !dados.liberados || dados.flashcards.length === 0) return null

  return (
    <section aria-label="Números que caem" className="space-y-4">
      <h2
        className="revela font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600"
        style={{ "--atraso": "280ms" } as CSSProperties}
      >
        {dados.total} números que caem
      </h2>
      {aberto ? (
        <Baralho cartas={dados.flashcards} aoFechar={() => setAberto(false)} />
      ) : (
        <div className="revela flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-navy-100 bg-white p-6 shadow-sm" style={{ "--atraso": "320ms" } as CSSProperties}>
          <p className="text-sm text-slate-600">
            Distâncias, prazos, idades e validades das normas — o tipo de questão que mais derruba. Um cartão por
            número, com o item da norma para conferir.
          </p>
          <Botao onClick={() => setAberto(true)}>Revisar os números</Botao>
        </div>
      )}
    </section>
  )
}

type Marcas = Record<number, "sei" | "rever">

function lerMarcas(): Marcas {
  try {
    return JSON.parse(localStorage.getItem(CHAVE) ?? "{}") as Marcas
  } catch {
    return {}
  }
}

function gravarMarcas(marcas: Marcas) {
  try {
    localStorage.setItem(CHAVE, JSON.stringify(marcas))
  } catch {
    // Sem armazenamento (janela privada): o baralho funciona igual, só não lembra.
  }
}

function embaralhar<T>(itens: T[]): T[] {
  const copia = [...itens]
  for (let i = copia.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copia[i], copia[j]] = [copia[j], copia[i]]
  }
  return copia
}

function Baralho({ cartas, aoFechar }: { cartas: Flashcard[]; aoFechar: () => void }) {
  const [marcas, setMarcas] = useState<Marcas>(lerMarcas)
  // Os que ainda não estão em "sei" vêm primeiro; o resto depois, para quem
  // quiser passar por tudo de novo.
  const fila = useMemo(() => {
    const pendentes = embaralhar(cartas.filter((c) => marcas[c.id] !== "sei"))
    const sabidos = embaralhar(cartas.filter((c) => marcas[c.id] === "sei"))
    return [...pendentes, ...sabidos]
    // Só na abertura: marcar não reordena no meio da sessão.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartas])
  const [indice, setIndice] = useState(0)
  const [virado, setVirado] = useState(false)

  const carta = fila[indice]
  const sabidos = cartas.filter((c) => marcas[c.id] === "sei").length

  function marcar(valor: "sei" | "rever") {
    const novas = { ...marcas, [carta.id]: valor }
    setMarcas(novas)
    gravarMarcas(novas)
    setVirado(false)
    setIndice((i) => (i + 1) % fila.length)
  }

  function zerar() {
    setMarcas({})
    gravarMarcas({})
    setIndice(0)
    setVirado(false)
  }

  return (
    <div className="revela rounded-2xl border border-navy-100 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <span>
          {indice + 1} de {fila.length} · <span className="font-semibold text-emerald-700">{sabidos} que você já sabe</span>
        </span>
        <span className="flex gap-3">
          <button type="button" onClick={zerar} className="underline">
            zerar
          </button>
          <button type="button" onClick={aoFechar} className="underline">
            fechar
          </button>
        </span>
      </div>

      <button
        type="button"
        onClick={() => setVirado((v) => !v)}
        className="mt-4 block w-full rounded-xl border border-gold-500/60 bg-gold-500/10 p-6 text-left transition hover:bg-gold-500/15"
        aria-label={virado ? "Ver a pergunta" : "Ver a resposta"}
      >
        <p className="font-mono text-[0.62rem] uppercase tracking-wider text-gold-700">
          {carta.unidade} · {virado ? "resposta" : "pergunta"}
        </p>
        {virado ? (
          <>
            <p className="mt-2 text-lg font-semibold text-navy-900">{carta.resposta}</p>
            <p className="mt-3 font-mono text-[0.7rem] text-slate-500">{carta.fonte}</p>
          </>
        ) : (
          <>
            <p className="mt-2 text-lg font-semibold text-navy-900">{carta.pergunta}</p>
            <p className="mt-3 text-xs text-slate-500">Toque para ver a resposta.</p>
          </>
        )}
      </button>

      {virado && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Botao onClick={() => marcar("sei")}>Sei</Botao>
          <Botao variante="fantasma" onClick={() => marcar("rever")}>
            Rever depois
          </Botao>
        </div>
      )}
    </div>
  )
}
